import { useUserStore } from '@/stores/modules/user'
import type { BeforePeakEquipType, EquipItemType, EquipQuality, EquipType } from '@/types/equip'
import type { UserAttrType } from '@/types/user'
import { computed } from 'vue'

export const equipIcon = {
  weapon: '🔪',
  helmet: '🧢',
  armor: '🥋',
  shoes: '🥾',
  jewelry: '💍',
  watermelon: '🍉',
  battery: '🔋',
  pants: '👖',
  glove: '🧤',
  necklace: '📿',
  belt: '🎗️',
  medal: '🥇',
}
export const equipIconRogue = {
  ...equipIcon,
  watermelon: '🍉',
  battery: '🔋',
}
export const equipQualityText: Record<string, string> = {
  normal: '普通',
  race: '稀有',
  epic: '史诗',
  unique: '独特',
  legend: '传说',
  antiquity: '远古',
  peerless: '绝世',
  darkGold: '暗金',
  myth: '神话暗金',
  supreme: '至臻',
}

export const equipTypeList = computed<EquipType[] | BeforePeakEquipType[]>(() => {
  const userStore = useUserStore()
  if (userStore.soulType > 3) {
    return ['weapon', 'helmet', 'armor', 'belt', 'glove', 'pants', 'shoes', 'necklace', 'jewelry', 'medal']
  } else {
    return ['weapon', 'helmet', 'armor', 'shoes', 'jewelry']
  }
})

export const equipQualityList: EquipQuality[] = ['normal', 'race', 'epic', 'unique', 'legend', 'antiquity', 'peerless', 'darkGold', 'myth', 'supreme']

export const batchSellList = [
  { name: '普通', key: 'normal' },
  { name: '稀有', key: 'race' },
  { name: '史诗', key: 'epic' },
  { name: '独特', key: 'unique' },
  { name: '传说', key: 'legend' },
  { name: '绝世', key: 'peerless' },
  { name: '暗金', key: 'darkGold' },
]

const reinforceAttr: Record<string, Record<number, number>> = {
  atk: { 1: 2, 2: 3, 3: 4, 4: 6, 5: 8, 6: 10, 7: 12, 8: 14, 9: 17, 10: 21, 11: 24, 12: 27, 13: 30, 14: 33, 15: 36, 16: 36, 17: 36, 18: 36 },
  heat: { 1: 1, 2: 2, 3: 3, 4: 4, 5: 6, 6: 7, 7: 9, 8: 11, 9: 13, 10: 16, 11: 18, 12: 20, 13: 22, 14: 24, 15: 26, 16: 26, 17: 26, 18: 26 },
  rate: {
    1: 0.4,
    2: 0.8,
    3: 1.2,
    4: 1.8,
    5: 2.4,
    6: 3.2,
    7: 4.0,
    8: 4.5,
    9: 5,
    10: 6,
    11: 6.5,
    12: 7,
    13: 7.5,
    14: 8,
    15: 8.5,
    16: 8.5,
    17: 8.5,
    18: 8.5,
  },
  crtd: { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5.5, 6: 7, 7: 8.5, 8: 10, 9: 12, 10: 13.5, 11: 15, 12: 16.5, 13: 18, 14: 19.5, 15: 21, 16: 21, 17: 21, 18: 21 },
}
export function getReinforceAttr(type: EquipType, reinforcedLevel: number): Partial<UserAttrType> {
  const extend: { ad?: number } = {}
  if (reinforcedLevel > 15) {
    extend.ad = Number(((reinforcedLevel - 15) * 0.2).toFixed(1))
  }
  switch (type) {
    case 'weapon':
      return { atk: reinforceAttr.atk[reinforcedLevel], ...extend }
    case 'helmet':
      return { heat: reinforceAttr.heat[reinforcedLevel], ...extend }
    case 'armor':
      return { hr: reinforceAttr.rate[reinforcedLevel], ...extend }
    case 'shoes':
      return { atksp: reinforceAttr.rate[reinforcedLevel], ...extend }
    case 'jewelry':
      return { crt: reinforceAttr.rate[reinforcedLevel], crtd: reinforceAttr.crtd[reinforcedLevel], ...extend }
    default:
      return {}
  }
}

export const starAttrList: Record<string, Partial<UserAttrType>> = {
  1: {
    atk: 2,
  },
  2: {
    atksp: 1,
  },
  3: {
    crt: 2,
  },
  4: {
    crtd: 6,
  },
  5: {
    heat: 3,
  },
  6: {
    hr: 1,
  },
}

export function getRiseStarAttr(starAttrs: number[]) {
  return starAttrs.map((item) => starAttrList[item])
}

export const RuneRoleText: Record<number, string> = {
  1: '战争之神',
  2: '智慧之神',
  3: '狩猎之神',
}

export const batchSellRuneList = [
  { name: '普通', key: 'normal' },
  { name: '稀有', key: 'race' },
  { name: '史诗', key: 'epic' },
]

export const relicIcon: Record<string, string> = {
  wind: '🌪️',
  fire: '🔥',
  water: '💧',
  soil: '🌱',
}
export const relicElementNameMap: Record<string, string> = {
  wind: '风',
  fire: '火',
  water: '水',
  soil: '土',
}
export const elementRestraint: Record<string, string> = {
  wind: 'fire',
  fire: 'water',
  water: 'soil',
  soil: 'wind',
}

export const itemPageTypeMap = {
  equip: '装备',
  rune: '符石',
  relic: '圣物',
  skill: '技能',
}

export const supremeAffixLevelQualityMap: Record<number, string> = {
  1: 'race',
  2: 'epic',
  3: 'legend',
  4: 'antiquity',
}

export const supremeLevelQualityMap: Record<number, { classes: string; text: string }> = {
  1: {
    classes: 'normal',
    text: 'D',
  },
  2: {
    classes: '',
    text: 'C',
  },
  3: {
    classes: 'race',
    text: 'B',
  },
  4: {
    classes: 'epic',
    text: 'A',
  },
  5: {
    classes: 'legend',
    text: 'S',
  },
}

export const noPercentEquipAttrKeyList: (keyof UserAttrType)[] = [
  'atk',
  'heat',
  'voidDef',
  'mp',
  'mpRegen',
  'windSkillGrow',
  'fireSkillGrow',
  'waterSkillGrow',
  'soilSkillGrow',
  'allSkillGrow',
]
